<template>
  <v-container class="pa-0" fluid>
    <mf-card-container
      title="Meus ajustes de cashback"
      description="Aqui você pode creditar ou debitar cashback de um consumidor da sua bandeira. Abaixo você confere os ajustes que já foram realizados."
    >
      <template v-slot:filters>
        <headers-data-table-settings
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          headers-settings-name="cashback-adjustments-headers-configs"
        />
        <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <export-email-report-dialog
                title="Enviar relatório de cashback"
                description="O relatório de ajustes de cashback é enviado, através de um e-mail, para a caixa de entrada dos endereços de e-mail adicionados abaixo. Um arquivo CSV ou XLSX contendo todas as informações detalhadas dos ajustes de cashback é enviado em anexo ao e-mail."
                :exportReportFn="exportReport"
                :disabled="!hasPiiAccess"
                async
                @file-format="getFileFormat"
              />
            </span>
          </template>
          <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
        </v-tooltip>
        <mf-search
          v-model="search"
          placeholder="Busque por CPF/CNPJ ou nome"
          hide-details
          :disabled="!hasPiiAccess"
          :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
        />
      </template>

      <paginated-data-table
        ref="table"
        :headers="computedHeaders"
        :search="search"
        :query="query"
        :custom-items-per-page="10"
        :queryName="'cashbackAdjustments'"
        :queryMainPropertyName="'cashback_adjustments'"
        :queryVariables="queryVariables"
        default-table-sort-by="created_at"
        :default-table-sort-desc="true"
        :graphql-uri="$microservicesUrls['cashback']"
        :loading="loading"
        @update:maxRecords="updateMaxRecords"
      >
        <template v-slot:item.document_no="{ item }">
          <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <template v-if="hasPiiAccess">
                  {{ getSafe(() => item.user.document_no) | cpfCnpj }}
                </template>
                <template v-else>
                  {{ item.user.document_no }}
                </template>
              </span>
            </template>
            <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
          </v-tooltip>
        </template>
        <template v-slot:item.name="{ item }">
          <v-tooltip v-if="hasPiiAccess" top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ getSafe(() => item.user.name) | hifen | truncate({ length: 24 }) }}</span>
            </template>
            <span>{{ getSafe(() => item.user.name) }}</span>
          </v-tooltip>
          <v-tooltip v-else max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <span>{{ getSafe(() => item.user.name) }}</span>
              </span>
            </template>
            <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
          </v-tooltip>
        </template>
        <template v-slot:item.previous_inactive_cashback="{ item }">
          {{ item.previous_inactive_cashback | localeString(2) | currency }}
        </template>
        <template v-slot:item.final_inactive_cashback="{ item }">
          {{ item.final_inactive_cashback | localeString(2) | currency }}
        </template>
        <template v-slot:item.adjustment_amount="{ item }">
          {{ item.adjustment_amount | localeString(2) | currency }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy HH:mm:ss') }}
        </template>
        <template v-slot:item.expires_at="{ item }">
          {{ item.expires_at ? DateTime.fromISO(item.expires_at).toFormat('dd/MM/yyyy') : '-' }}
        </template>
      </paginated-data-table>
    </mf-card-container>
  </v-container>
</template>

<script>
// TODO: Retrigger load after adjustment.

import PaginatedDataTable from '@/components/PaginatedDataTable'
import { QUERY_GET_CASHBACK_ADJUSTMENTS, MUTATION_CASHBACK_ADJUSTMENTS_REPORT } from '@/modules/mobile/cashback/graphql'
import { DateTime } from 'luxon'
import cloneDeep from 'lodash/cloneDeep'
import ExportEmailReportDialog from '@/components/export-email-report/ExportEmailReportDialog.vue'
import headersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
export default {
  name: 'CashbackAdjustmentsTable',
  components: {
    PaginatedDataTable,
    ExportEmailReportDialog,
    headersDataTableSettings,
  },
  data: () => ({
    search: '',
    loading: false,
    maxRecords: 0,
    fileFormat: 'CSV',
    exportLoading: false,
    headers: [],
  }),
  computed: {
    DateTime() {
      return DateTime
    },
    query() {
      return QUERY_GET_CASHBACK_ADJUSTMENTS
    },
    queryVariables() {
      return {
        search: this.search,
      }
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          sortable: header.hasOwnProperty('sortable') ? header.sortable : true,
        }
      })
    },
    defaultHeaders() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'document_no',
          width: '13%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '12%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Saldo anterior',
          value: 'previous_inactive_cashback',
          width: '8%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Saldo posterior',
          value: 'final_inactive_cashback',
          width: '8%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Ajuste',
          value: 'adjustment_amount',
          width: '8%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Motivo',
          value: 'adjustment_reason',
          width: '8%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Ajustado por',
          value: 'adjusted_by.login',
          width: '15%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Data do ajuste',
          value: 'created_at',
          width: '17%',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Expiração do cashback',
          value: 'expires_at',
          width: '18%',
          sortable: false,
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
      ]
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  mounted() {
    if (this.headers?.length <= 0) {
      this.headers = cloneDeep(this.defaultHeaders)
    }
  },
  methods: {
    reload() {
      this.$refs.table.getData()
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
    updateMaxRecords(records) {
      this.maxRecords = records
    },
    async exportReport(emails) {
      return this.$apollo.mutate({
        mutation: MUTATION_CASHBACK_ADJUSTMENTS_REPORT,
        fetchPolicy: 'no-cache',
        variables: {
          emails,
          fileFormat: this.fileFormat,
        },
        context: {
          uri: this.$microservicesUrls['asyncJobs'],
        },
      })
    },
    getFileFormat(value) {
      this.fileFormat = value
    },
    async getDataToExport() {
      try {
        this.loading = true
        const {
          data: { cashbackAdjustments },
        } = await this.$apollo.query({
          query: this.query,
          variables: {
            ...this.queryVariables,
            page: 1,
            pageSize: this.maxRecords,
            sortBy: ['created_at'],
            sortDirection: ['DESC'],
          },
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        const headers = this.headers.map((h) => h.text)
        const rows = cashbackAdjustments.cashback_adjustments.map((item) => {
          return [
            item.user.document_no,
            item.user.name,
            item.previous_inactive_cashback.toFixed(2),
            item.final_inactive_cashback.toFixed(2),
            item.adjustment_amount.toFixed(2),
            item.adjustment_reason,
            item.adjusted_by.login,
            DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy HH:mm:ss'),
          ]
        }, [])
        return { headers, rows }
      } catch (error) {
        this.$snackbar({ message: 'Erro ao exportar .csv', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.export-csv {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.01em;
  color: $marca-0-l40 !important;
  text-transform: none;
  -webkit-text-decoration-line: none;
  text-decoration-line: none;
  align-self: center;
}

.search-and-export-header {
  display: flex !important;
  justify-content: flex-end;
}

.search-and-export-header > span:first-child {
  align-self: center;
  margin: 0 8px 0 8px;
}

.search-and-export-header > div {
  width: 100%;
  max-width: 300px;
  min-width: 110px;
}
</style>
